import { useEffect, useRef, useState } from "react";
import { useParams } from "react-router-dom";
import SignatureCanvas from "react-signature-canvas";
import { toast } from "sonner";
import { Loader2, CheckCircle2, Scale, PenLine } from "lucide-react";
import { api, apiError } from "@/lib/api";

const money = (n) => `$${Number(n || 0).toLocaleString("en-NZ", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;

export default function SettlementAccept() {
  const { token } = useParams();
  const [data, setData] = useState(null);
  const [error, setError] = useState("");
  const [name, setName] = useState("");
  const [busy, setBusy] = useState(false);
  const [accepted, setAccepted] = useState(false);
  const sigRef = useRef(null);

  useEffect(() => {
    api.get(`/settlement/${token}`)
      .then((r) => { setData(r.data); if (r.data.status === "Accepted") setAccepted(true); })
      .catch((e) => setError(apiError(e.response?.data?.detail) || "This settlement link is not valid."));
  }, [token]);

  const accept = async () => {
    if (!name.trim()) return toast.error("Please enter your full name.");
    if (!sigRef.current || sigRef.current.isEmpty()) return toast.error("Please add your signature.");
    setBusy(true);
    try {
      await api.post(`/settlement/${token}/accept`, { name, signature: sigRef.current.toDataURL("image/png") });
      setAccepted(true);
      toast.success("Settlement accepted. Thank you.");
    } catch (e) { toast.error(apiError(e.response?.data?.detail)); } finally { setBusy(false); }
  };

  return (
    <div className="min-h-screen bg-[#0A0D0B] text-[#F5F3EC] flex flex-col">
      <header className="border-b border-[#233027] px-5 md:px-10 py-5 flex items-center gap-3">
        <Scale size={22} className="text-[#B89874]" strokeWidth={1.5} />
        <span className="font-display text-2xl tracking-tight">Grafton Group</span>
      </header>

      <main className="flex-1 flex items-center justify-center px-5 py-12">
        {error ? (
          <div data-testid="settlement-error" className="max-w-md text-center text-[#A3ACA6]">{error}</div>
        ) : !data ? (
          <Loader2 className="animate-spin text-[#B89874]" />
        ) : (
          <div className="w-full max-w-2xl bg-[#121814] border border-[#233027] p-7 md:p-10" data-testid="settlement-card">
            <div className="text-[11px] uppercase tracking-[0.3em] text-[#B89874]">Full &amp; final settlement</div>
            <h1 className="font-display text-3xl md:text-4xl mt-3 leading-tight">Settlement offer for {data.party_name}</h1>
            <p className="mt-4 text-[#A3ACA6] leading-relaxed">
              Our client, <b className="text-[#F5F3EC]">{data.client_name}</b>, has agreed to accept the sum below in full and
              final settlement of the outstanding debt. On payment by the due date, the balance shown will be written off and
              no further action will be taken.
            </p>

            <div className="mt-7 grid grid-cols-2 gap-px bg-[#233027] border border-[#233027]">
              {[
                ["Original debt", money(data.original_amount)],
                ["Settlement amount", money(data.settlement_amount)],
                ["To be written off", money(data.writeoff_amount)],
                [data.settlement_type === "instalments" ? "Final instalment by" : "Pay on or before", data.payment_due_date || "—"],
              ].map(([k, v]) => (
                <div key={k} className="bg-[#0C110E] p-4">
                  <div className="text-[10px] uppercase tracking-[0.16em] text-[#58635C]">{k}</div>
                  <div className="mt-1 font-display text-xl text-[#F5F3EC]">{v}</div>
                </div>
              ))}
            </div>

            {data.settlement_type === "instalments" && data.instalments?.length > 0 && (
              <div className="mt-4" data-testid="settlement-schedule">
                <div className="text-[10px] uppercase tracking-[0.16em] text-[#58635C] mb-2">Instalment schedule</div>
                <div className="border border-[#233027]">
                  {data.instalments.map((s, i) => (
                    <div key={i} className="flex items-center justify-between px-4 py-2.5 border-b border-[#233027] last:border-0 text-sm">
                      <span className="text-[#A3ACA6]">Instalment {i + 1} · {new Date(s.due_date).toLocaleDateString("en-NZ", { day: "2-digit", month: "short", year: "numeric" })}</span>
                      <span className="flex items-center gap-3">
                        <span className="font-display text-[#F5F3EC]">{money(s.amount)}</span>
                        {s.status === "paid" && <span className="text-[10px] uppercase tracking-wide text-[#7FB08C]">Paid</span>}
                      </span>
                    </div>
                  ))}
                </div>
              </div>
            )}
            <p className="mt-4 text-xs text-[#58635C]">
              Payment to {data.bank}. Please quote reference {data.reference || "—"}.
            </p>

            {accepted ? (
              <div data-testid="settlement-accepted" className="mt-8 flex items-start gap-3 border border-[#3F6B4E]/50 bg-[#12261B] p-5">
                <CheckCircle2 className="text-[#7FB08C] shrink-0 mt-0.5" />
                <div>
                  <div className="font-semibold text-[#F5F3EC]">Settlement accepted</div>
                  <div className="text-sm text-[#A3ACA6] mt-1">
                    Thank you{data.accepted_name ? `, ${data.accepted_name}` : ""}. Please arrange payment by the due date to
                    complete the settlement. A confirmation has been recorded.
                  </div>
                </div>
              </div>
            ) : (
              <div className="mt-8">
                <label className="text-[11px] uppercase tracking-[0.18em] text-[#A3ACA6]">Your full name</label>
                <input data-testid="settlement-name" value={name} onChange={(e) => setName(e.target.value)}
                  className="mt-2 w-full bg-[#0A0D0B] border border-[#233027] px-4 py-3 text-[#F5F3EC] focus:border-[#B89874] outline-none" />
                <label className="mt-5 block text-[11px] uppercase tracking-[0.18em] text-[#A3ACA6]">Sign to accept these terms</label>
                <div className="mt-2 bg-white border border-[#233027]">
                  <SignatureCanvas ref={sigRef} penColor="#0A0D0B" canvasProps={{ className: "w-full h-40", "data-testid": "settlement-signature" }} />
                </div>
                <button type="button" onClick={() => sigRef.current && sigRef.current.clear()} data-testid="settlement-clear"
                  className="mt-2 text-xs text-[#58635C] hover:text-[#B89874] transition-colors">Clear signature</button>
                <button onClick={accept} disabled={busy} data-testid="settlement-accept"
                  className="mt-6 w-full inline-flex items-center justify-center gap-2 bg-[#B89874] text-[#0A0D0B] px-8 py-4 text-sm font-semibold uppercase tracking-[0.12em] hover:bg-[#C9A780] transition-colors disabled:opacity-60">
                  {busy ? <Loader2 size={16} className="animate-spin" /> : <PenLine size={16} />} Accept settlement
                </button>
              </div>
            )}
          </div>
        )}
      </main>
    </div>
  );
}
